import axios from "@/api/axiosInstance";
import {
  FetchCompanyDiagnosticsRequest,
  FetchCompanyDiagnosticsResponse,
} from "./types";
import type { CompanyDiagnosticsResult } from "../../types/diagnostic";

/**
 * One page of a company's diagnostic history, newest first.
 *
 * Paged server-side: every attempt in this payload carries a full sections →
 * questions → choices tree, so it is the largest response the feature can
 * produce and it grows every time the diagnostic is run again. `meta` carries
 * the totals for the whole history — the row count is only ever the page.
 *
 * `company` comes back null for a company that exists but has never been
 * diagnosed; only a company row that does not exist at all is a 404.
 */
export const fetchCompanyDiagnostics = async (
  companyId: number,
  params: FetchCompanyDiagnosticsRequest = {},
  signal?: AbortSignal,
): Promise<CompanyDiagnosticsResult> => {
  const response = await axios.get<FetchCompanyDiagnosticsResponse>(
    `/api/private/diagnostics/companies/${companyId}`,
    { params, signal },
  );
  const { company, attempts } = response.data.data;
  return {
    company,
    attempts,
    // Falling back to the row count rather than to 0: a missing envelope would
    // otherwise report a company with rows on screen as having no history.
    totalCount: response.data.meta?.totalCount ?? attempts.length,
    hasMore: response.data.meta?.hasMore ?? false,
  };
};
